import { matchActivitiesWithAI } from 'api/openai/openaiAPI';

interface ActivityItem {
	id: number;
	title: string;
}

export interface ActivityMapping {
	oldActivity: ActivityItem;
	newActivityId: number | null;
	confidence: 'exact' | 'contains' | 'ai' | 'single' | 'manual';
	commentCount: number;
}

/**
 * Normalize a string for comparison: lowercase, trim, remove punctuation
 */
function normalize(s: string): string {
	return s.toLowerCase().trim().replace(/[^a-z0-9\s]/g, '');
}

/**
 * Check if two strings are a "contains" match (one contains a significant word from the other)
 */
function containsMatch(a: string, b: string): boolean {
	const na = normalize(a);
	const nb = normalize(b);
	if (na.includes(nb) || nb.includes(na)) return true;

	// Check significant words (3+ chars)
	const wordsA = na.split(/\s+/).filter((w) => w.length >= 3);
	const wordsB = nb.split(/\s+/).filter((w) => w.length >= 3);
	return wordsA.some((w) => nb.includes(w)) || wordsB.some((w) => na.includes(w));
}

/**
 * Build activity mappings using tiered matching strategy:
 * 1. Single activity shortcut
 * 2. Exact match (case-insensitive)
 * 3. Contains match
 * 4. AI semantic match for remaining
 * 5. Manual for anything still unmatched
 */
export async function buildActivityMappings(
	oldActivities: ActivityItem[],
	commentCountByActivity: Map<number, number>,
	newActivities: ActivityItem[],
): Promise<ActivityMapping[]> {
	const mappings: ActivityMapping[] = oldActivities.map((old) => ({
		oldActivity: old,
		newActivityId: null,
		confidence: 'manual' as const,
		commentCount: commentCountByActivity.get(old.id) ?? 0,
	}));

	// Tier 1: Single activity shortcut
	if (newActivities.length === 1) {
		return mappings.map((m) => ({
			...m,
			newActivityId: newActivities[0].id,
			confidence: 'single' as const,
		}));
	}

	// Tier 2: Exact match (case-insensitive)
	for (const mapping of mappings) {
		const exact = newActivities.find(
			(n) => normalize(n.title) === normalize(mapping.oldActivity.title),
		);
		if (exact) {
			mapping.newActivityId = exact.id;
			mapping.confidence = 'exact';
		}
	}

	// Tier 3: Contains match for unmatched
	for (const mapping of mappings) {
		if (mapping.newActivityId !== null) continue;
		const match = newActivities.find((n) => containsMatch(n.title, mapping.oldActivity.title));
		if (match) {
			mapping.newActivityId = match.id;
			mapping.confidence = 'contains';
		}
	}

	// Tier 4: AI matching for remaining unmatched
	const unmatched = mappings.filter((m) => m.newActivityId === null);
	if (unmatched.length > 0) {
		try {
			const aiMap = await matchActivitiesWithAI(
				unmatched.map((m) => m.oldActivity),
				newActivities,
			);
			for (const mapping of unmatched) {
				const aiMatch = aiMap[mapping.oldActivity.id];
				if (aiMatch && newActivities.some((n) => n.id === aiMatch)) {
					mapping.newActivityId = aiMatch;
					mapping.confidence = 'ai';
				}
			}
		} catch {
			// AI failed, leave as manual
		}
	}

	return mappings;
}
